import * as editorActions from '../actions/course.actions';
import * as actions from '../actions/undo.actions';
import {diff, applyChange, revertChange} from 'deep-diff';
import {getCanRedo, getCanUndo} from '../selectors/undo.selectors';
import {getActivePageId} from '~/stores/player/selectors/course.selectors';

/** Reset undo on page change. */
const RESET_ON_PAGE_CHANGE = true;

/** Max amount of undo steps before old ones get overwritten. */
const MAX_UNDO = 50;

/** Time that has to pass before a new undo entry is created for a repeated action. */
const MERGE_TIME_OFFSET = 1500; // 1.5s

const MERGE_ACTIONS = new Set([editorActions.SET_ACTIVE_PAGE_ID, editorActions.SET_ACTIVE_SLIDE_ID]);

const initialUndo = {
  history: [],
  index: -1,
  previousType: null,
  previousTime: 0,
  pageId: -1,
};

export const undoReducer = reducer => ({undo = initialUndo, ...previousState}, action) => {
  const nextState = reducer(previousState, action);

  // Always return state including undo object. Passed reducer does not acknowledge the undo state.
  const state = {
    ...nextState,
    undo,
  };

  const {
    history,
    index,
    previousType,
    previousTime,
  } = state.undo;

  switch (action.type) {
  // Actions that create an undo step. Only the actions specified here will be undoable.
  case editorActions.SET_ACTIVE_PAGE_ID:
  case editorActions.SET_ACTIVE_SLIDE_ID:
  case editorActions.ADD_PAGE:
  case editorActions.DELETE_PAGE:
  case editorActions.SET_PAGE_DATA:
  case editorActions.SET_SLIDE_DATA: {
    if (index < history.length - 1 && MERGE_ACTIONS.has(action.type)) {
      // Don't overwrite redo history.
      return state;
    }

    const entry = diff(previousState, nextState);

    if (!entry) {
      // No changes. No need to create undo step.
      return state;
    }

    const previousStep = index !== -1 && history[index];
    const currentTime = Date.now();

    const shouldMergePrevious = previousStep && (MERGE_ACTIONS.has(action.type) || action.type === previousType && currentTime - previousTime < MERGE_TIME_OFFSET);

    const startIndex = Math.max(index - MAX_UNDO, 0);
    const activePageId = getActivePageId(nextState);

    if (RESET_ON_PAGE_CHANGE && activePageId !== -1 && activePageId !== state.undo.pageId) {
      return {
        ...state,
        undo: {
          ...initialUndo,
          pageId: activePageId,
        },
      };
    }

    return {
      ...state,
      undo: {
        history: shouldMergePrevious ? [...history.slice(startIndex, index), [...previousStep, ...entry]] : [...history.slice(startIndex, index + 1), entry],
        index: shouldMergePrevious ? index : index + 1,
        previousType: shouldMergePrevious ? previousType : action.type,
        previousTime: shouldMergePrevious ? previousTime : currentTime,
        pageId: activePageId === -1 ? state.undo.pageId : activePageId,
      },
    };
  }
  case actions.UNDO: {
    if (!getCanUndo(state)) {
      return state;
    }

    const entry = history?.[index] ?? [];

    [...entry || []]?.reverse?.()?.forEach?.(change => {
      revertChange(state, true, change);
    });

    return {
      ...state,
      undo: {
        ...state.undo,
        index: index - 1,
      },
    };
  }
  case actions.REDO: {
    if (!getCanRedo(state)) {
      return state;
    }

    const entry = history[index + 1];

    entry.forEach(change => {
      applyChange(state, true, change);
    });

    return {
      ...state,
      undo: {
        ...state.undo,
        index: index + 1,
      },
    };
  }
  default: {
    return state;
  }
  }
};